import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Trash2, Star, ExternalLink, CheckCircle2, Circle, Pencil } from 'lucide-react';
import { useLongPress } from '../hooks/useLongPress';

const openExternalLink = (url, event) => {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (isMobile) {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

// Componente de canción individual (simplificado para la vista de momentos)
const MomentoSongItem = ({ song, index, onRemove, showDetails = false, momentoId, isPlayed = false, onTogglePlayed, isLiveMode = false, onEditSong, dragDisabled = false }) => {
  const editLongPress = useLongPress(
    () => onEditSong && onEditSong(song),
    { delay: 600 }
  );
  return (
    <Draggable draggableId={`${momentoId}-${song.id}`} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 rounded transition-opacity ${
            song._inicio_hora_loca
              ? 'bg-pink-950/40 border border-pink-500/50 ring-1 ring-pink-500/30'
              : song._imprescindible
              ? 'bg-amber-950/30 border border-amber-500/40 ring-1 ring-amber-500/20'
              : song._de_spotify_cliente
              ? 'bg-green-950/30 border border-green-500/30'
              : 'bg-slate-800/50 border border-slate-700'
          } ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-cyan-500 opacity-90 z-50' : ''
          } ${isPlayed ? 'opacity-50 bg-slate-800/30' : ''}`}
          data-testid={`momento-song-${song.id}`}
          data-played={isPlayed ? 'true' : 'false'}
          data-imperdible={song._imprescindible ? 'true' : 'false'}
          data-inicio-hora-loca={song._inicio_hora_loca ? 'true' : 'false'}
        >
          {/* Drag handle */}
          <div
            {...provided.dragHandleProps}
            className={`p-0.5 flex-shrink-0 ${dragDisabled ? 'text-slate-700 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300'}`}
            title={dragDisabled ? 'Muestra las reproducidas para reordenar' : 'Arrastra para reordenar o mover a otro momento'}
          >
            <GripVertical className="w-3 h-3 sm:w-4 sm:h-4" />
          </div>

          {/* Index number - posición global */}
          {song._posicionGlobal && (
            <span className="text-cyan-400 text-xs sm:text-sm flex-shrink-0 w-5 sm:w-6 font-mono">
              {song._posicionGlobal}.
            </span>
          )}

          {/* Song info */}
          <div className="flex-1 min-w-0">
            <div
              className={`flex items-baseline gap-1 flex-wrap select-none transition-transform ${
                onEditSong ? 'cursor-pointer touch-manipulation' : ''
              } ${editLongPress.isPressing ? 'scale-[0.97] opacity-80' : ''}`}
              {...(onEditSong ? editLongPress.bindings : {})}
              title={onEditSong ? 'Mantén pulsado 0,6s para editar la canción' : undefined}
              data-testid={`momento-song-title-area-${song.id}`}
            >
              {/* Iter89.4: Badges de marcadores */}
              {song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-pink-600/30 text-pink-300 border border-pink-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="Canción de inicio de Hora Loca"
                  data-testid={`badge-inicio-hora-loca-${song.id}`}
                >
                  🎉 INICIO HL
                </span>
              )}
              {song._imprescindible && !song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-amber-500/25 text-amber-300 border border-amber-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="Canción imperdible (cliente la pidió)"
                  data-testid={`badge-imperdible-${song.id}`}
                >
                  ⭐ IMPERDIBLE
                </span>
              )}
              {song._de_spotify_cliente && !song._imprescindible && !song._inicio_hora_loca && (
                <span
                  className="px-1.5 py-0.5 bg-green-600/25 text-green-300 border border-green-500/40 rounded text-[10px] font-bold flex-shrink-0"
                  title="De la playlist Spotify del cliente"
                  data-testid={`badge-spotify-cliente-${song.id}`}
                >
                  🎵 SPOTIFY
                </span>
              )}
              <p className={`text-white font-medium text-xs sm:text-sm truncate flex-shrink ${isPlayed ? 'line-through text-slate-400' : ''}`}>
                {song.titulo || song.title || 'Sin título'}
              </p>
              {onEditSong && (
                <Pencil
                  className="w-3 h-3 text-slate-500 hover:text-cyan-400 transition-colors flex-shrink-0 hidden sm:inline-block"
                  aria-hidden="true"
                />
              )}
              <span className="text-slate-500 text-xs flex-shrink-0">•</span>
              <p className={`text-slate-400 text-xs truncate flex-shrink ${isPlayed ? 'line-through' : ''}`}>
                {song.artista || song.artist || 'Artista desconocido'}
              </p>
            </div>

            {showDetails && (
              <div className="flex items-center gap-1 mt-0.5 overflow-x-auto scrollbar-hide">
                {song.rating > 0 && (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {[...Array(Math.min(song.rating, 5))].map((_, i) => (
                      <Star key={i} className="w-2 h-2 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                )}
                {song.favorito && <span className="text-xs flex-shrink-0">❤️</span>}
                {song.genres && song.genres.slice(0, 1).map((genre) => (
                  <span key={genre} className="px-1 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-[10px] flex-shrink-0">
                    {genre}
                  </span>
                ))}
                {song.energia && (
                  <span className={`px-1 py-0.5 rounded text-[10px] flex-shrink-0 ${
                    song.energia === 'Muy Alta' ? 'bg-red-900/30 text-red-400' :
                    song.energia === 'Alta' ? 'bg-orange-900/30 text-orange-400' :
                    song.energia === 'Media' ? 'bg-yellow-900/30 text-yellow-400' :
                    'bg-blue-900/30 text-blue-400'
                  }`}>
                    {song.energia === 'Muy Alta' ? '⚡⚡⚡⚡' :
                     song.energia === 'Alta' ? '⚡⚡⚡' :
                     song.energia === 'Media' ? '⚡⚡' : '⚡'}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-0.5 flex-shrink-0">
            {/* Played toggle (marca canción como reproducida) */}
            {onTogglePlayed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePlayed(song.id);
                }}
                className={`${isLiveMode ? 'p-2 min-w-[44px]' : 'p-1'} rounded transition-colors ${
                  isPlayed
                    ? 'bg-emerald-600/40 hover:bg-emerald-600/60'
                    : 'bg-slate-700/40 hover:bg-emerald-600/30'
                }`}
                title={isPlayed ? 'Desmarcar como reproducida' : 'Marcar como reproducida'}
                data-testid={`momento-toggle-played-btn-${song.id}`}
                aria-pressed={isPlayed}
              >
                {isPlayed ? (
                  <CheckCircle2 className={`${isLiveMode ? 'w-5 h-5' : 'w-3 h-3'} text-emerald-300`} />
                ) : (
                  <Circle className={`${isLiveMode ? 'w-5 h-5' : 'w-3 h-3'} text-slate-400`} />
                )}
              </button>
            )}
            {song.spotify_url && (
              <button
                onClick={(e) => openExternalLink(song.spotify_url, e)}
                className="p-1 rounded bg-green-600/20 hover:bg-green-600/40 transition-colors"
                title="Abrir en Spotify"
              >
                <ExternalLink className="w-3 h-3 text-green-400" />
              </button>
            )}
            {onRemove && !isLiveMode && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(song.id);
                }}
                className="p-1 rounded bg-red-600/20 hover:bg-red-600/40 transition-colors"
                title="Eliminar"
              >
                <Trash2 className="w-3 h-3 text-red-400" />
              </button>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

// Iconos y colores para cada momento
const MOMENTO_CONFIG = {
  'Primera Baile': { emoji: '💃', color: 'from-pink-600/30 to-purple-600/30', border: 'border-pink-600/50' },
  'Baile General': { emoji: '🎉', color: 'from-cyan-600/30 to-blue-600/30', border: 'border-cyan-600/50' },
  'Hora Loca': { emoji: '🔥', color: 'from-orange-600/30 to-red-600/30', border: 'border-orange-600/50' },
  'Cierre': { emoji: '🌙', color: 'from-indigo-600/30 to-slate-600/30', border: 'border-indigo-600/50' },
  'Sin Momento': { emoji: '🎵', color: 'from-slate-600/30 to-slate-700/30', border: 'border-slate-600/50' }
};

// Componente principal con drag-and-drop entre momentos
export const MomentosSortableView = ({ 
  groupedSongs, // Objeto { 'Primera Baile': [songs], 'Baile General': [songs], ... }
  onReorderGlobal, // Callback que recibe el nuevo array de IDs ordenados
  onRemove,
  showDetails = true,
  playedState = {},
  onTogglePlayed,
  isLiveMode = false,
  onEditSong,
  dragDisabled = false,
}) => {
  
  // Orden de los momentos
  const momentoOrder = ['Primera Baile', 'Baile General', 'Hora Loca', 'Cierre'];
  
  // Calcular el índice global de inicio para cada momento
  const getMomentoStartIndex = (targetMomento) => {
    let startIndex = 0;
    for (const momento of momentoOrder) {
      if (momento === targetMomento) break;
      startIndex += (groupedSongs[momento] || []).length;
    }
    return startIndex;
  };

  const handleDragEnd = (result) => {
    // Con "Ocultar reproducidas" activo se muestra un subconjunto: desactivamos el
    // reordenado para no reconstruir la lista sin las ocultas (evita pérdida).
    if (dragDisabled) return;
    const { source, destination } = result;
    
    // Si no hay destino, cancelar
    if (!destination) {
      console.log('⚠️ Drop cancelado');
      return;
    }
    
    // Si es la misma posición, no hacer nada
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('ℹ️ Misma posición');
      return;
    }
    
    console.log('🎯 Drag end:', {
      from: { momento: source.droppableId, index: source.index },
      to: { momento: destination.droppableId, index: destination.index }
    });
    
    // Obtener la canción que se está moviendo
    const sourceMomento = source.droppableId;
    const destMomento = destination.droppableId;
    const sourceSongs = groupedSongs[sourceMomento] || [];
    const movedSong = sourceSongs[source.index];
    
    if (!movedSong) {
      console.error('❌ No se encontró la canción a mover');
      return;
    }
    
    // Reconstruir la lista global completa
    let allSongs = [];
    for (const momento of momentoOrder) {
      allSongs = [...allSongs, ...(groupedSongs[momento] || [])];
    }
    
    // Calcular posiciones globales
    const sourceGlobalIndex = getMomentoStartIndex(sourceMomento) + source.index;
    let destGlobalIndex;
    
    if (sourceMomento === destMomento) {
      // Movimiento dentro del mismo momento
      destGlobalIndex = getMomentoStartIndex(destMomento) + destination.index;
    } else {
      // Movimiento entre momentos diferentes
      destGlobalIndex = getMomentoStartIndex(destMomento) + destination.index;
      
      // Ajustar si el destino está después del origen (porque eliminamos un elemento antes)
      if (destGlobalIndex > sourceGlobalIndex) {
        // No necesita ajuste para inserción después de remoción
      }
    }
    
    console.log('📍 Posiciones globales:', { source: sourceGlobalIndex, dest: destGlobalIndex });
    
    // Crear el nuevo orden
    const newOrder = allSongs.map(s => s.id);
    
    // Remover de la posición original
    newOrder.splice(sourceGlobalIndex, 1);
    
    // Ajustar el índice de destino si el origen estaba antes
    let adjustedDestIndex = destGlobalIndex;
    if (sourceGlobalIndex < destGlobalIndex) {
      adjustedDestIndex--;
    }
    
    // Insertar en la nueva posición
    newOrder.splice(adjustedDestIndex, 0, movedSong.id);
    
    console.log('✅ Nuevo orden generado:', { 
      movedSong: movedSong.title || movedSong.titulo,
      fromMomento: sourceMomento,
      toMomento: destMomento
    });
    
    // Llamar al callback con el nuevo orden
    onReorderGlobal(newOrder);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="space-y-4 max-h-96 overflow-y-auto pr-2" data-testid="momentos-sortable-view">
        <div className="flex items-center justify-between mb-2 sticky top-0 bg-slate-900/90 py-2 z-10">
          <p className="text-slate-400 text-sm">
            💡 Arrastra canciones entre secciones para cambiar su momento
          </p>
        </div>
        
        {momentoOrder.map((momento) => {
          const songs = groupedSongs[momento] || [];
          if (songs.length === 0) return null;
          
          const config = MOMENTO_CONFIG[momento] || MOMENTO_CONFIG['Sin Momento'];
          
          return (
            <Droppable key={momento} droppableId={momento}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`rounded-lg p-3 border transition-colors ${config.border} bg-gradient-to-r ${config.color} ${
                    snapshot.isDraggingOver ? 'ring-2 ring-cyan-400 bg-cyan-900/20' : ''
                  }`}
                  data-testid={`momento-section-${momento.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <h4 className="text-white font-semibold text-lg mb-3 flex items-center gap-2">
                    <span className="text-2xl">{config.emoji}</span>
                    {momento}
                    <span className="text-slate-400 text-sm font-normal ml-auto">
                      ({songs.length} {songs.length === 1 ? 'canción' : 'canciones'})
                    </span>
                  </h4>
                  
                  <div className="space-y-1">
                    {songs.map((song, index) => (
                      <MomentoSongItem
                        key={`${momento}-${song.id}`}
                        song={song}
                        index={index}
                        momentoId={momento}
                        onRemove={onRemove}
                        showDetails={showDetails}
                        isPlayed={!!playedState[song.id]?.played}
                        onTogglePlayed={onTogglePlayed}
                        isLiveMode={isLiveMode}
                        onEditSong={!isLiveMode ? onEditSong : undefined}
                        dragDisabled={dragDisabled}
                      />
                    ))}
                  </div>
                  
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default MomentosSortableView;
